/**
 * Charts of the object screen, drawn as SVG like the prototype: one scale per
 * axis, the hour axis of the local day (23 / 24 / 25 positions), a dashed
 * «зараз» line, a tooltip per hour. Width 960 units; the plot area leaves 58
 * units on each side so the windows timeline lines up with it.
 */
import { type ReactNode, useRef, useState } from 'react';
import type { DeyeWindow } from 'shared';

import { axisValues, stepDigits, valueScale } from './scale.js';

export const W = 960;
export const ML = 58;
export const MR = 58;
export const PW = W - ML - MR;

const C = {
  navy: '#04285d',
  sky: '#09a9f9',
  slate: '#8294ae',
  line: '#e3ecf5',
  ice: '#d4e8f9',
  text: '#66788f',
  band: '#f3f8fd',
};

const xh = (h: number, n: number) => ML + (h / n) * PW;
const niceMax = (v: number, step: number) => Math.max(step, Math.ceil(v / step) * step);

type Fmt = (v: number, digits?: number) => string;

/** Hover area over a chart: the hour (or bar) under the pointer gets a tooltip. */
function ChartBox({
  n,
  ml = ML,
  pw = PW,
  lines,
  children,
}: {
  n: number;
  ml?: number;
  pw?: number;
  lines: (i: number) => ReactNode[];
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ left: number; i: number } | null>(null);
  const move = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.floor(((x - ml) / pw) * n);
    if (i < 0 || i >= n) {
      setTip(null);
      return;
    }
    setTip({ i, left: Math.min(Math.max(e.clientX - rect.left, 110), Math.max(110, rect.width - 110)) });
  };
  const content = tip ? lines(tip.i) : [];
  return (
    <div
      className="chart-wrap"
      ref={ref}
      onPointerMove={move}
      // A tap shows the hour (touch has no hover); the pointer leaving hides it.
      onPointerDown={move}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') setTip(null);
      }}
    >
      {children}
      {tip && content.length > 0 ? (
        <div className="tooltip" style={{ left: tip.left, top: -6 }}>
          {content.map((l, k) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the lines of one tooltip never reorder
            <div key={k}>{l}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function HourAxis({ n, y, labels }: { n: number; y: number; labels: readonly string[] }) {
  const out: ReactNode[] = [];
  for (let h = 0; h <= n; h += 1) {
    out.push(
      <line key={`t${h}`} x1={xh(h, n)} x2={xh(h, n)} y1={y} y2={y + 4} stroke={C.slate} strokeWidth="1" />,
    );
    if (h % 3 === 0 && h < n) {
      out.push(
        <text key={`l${h}`} x={xh(h, n) + 2} y={y + 17} fontSize="12" fill={C.text}>
          {labels[h]}
        </text>,
      );
    }
  }
  return <>{out}</>;
}

function NowLine({
  n,
  at,
  top,
  bottom,
  label,
}: { n: number; at: number; top: number; bottom: number; label: string }) {
  const x = xh(at, n);
  return (
    <>
      <line x1={x} x2={x} y1={top} y2={bottom} stroke={C.sky} strokeWidth="1.5" strokeDasharray="4 3" />
      <text x={x + 5} y={top + 11} fontSize="12" fill={C.navy} fontWeight="500">
        {label}
      </text>
    </>
  );
}

export interface DayAxis {
  /** Hours of the day: 23 / 24 / 25. */
  n: number;
  /** Wall-clock label of each position, «HH:MM». */
  labels: readonly string[];
  /** Position of «now» in hours, fractional; null on another day. */
  now: number | null;
  nowLabel: string;
}

export function PriceChart({
  axis,
  prices,
  fmt,
  title,
  lines,
}: {
  axis: DayAxis;
  prices: readonly number[];
  fmt: Fmt;
  title: string;
  lines: (h: number) => ReactNode[];
}) {
  const { n } = axis;
  const H = 160;
  const MT = 14;
  const ph = H - MT - 26;
  const { step, max, digits } = valueScale(Math.max(...prices, 0.1) * 1.08, 3);
  const y = (v: number) => MT + ph - (v / max) * ph;
  const grid: ReactNode[] = [];
  for (const t of axisValues({ step, max })) {
    grid.push(<line key={`g${t}`} x1={ML} x2={W - MR} y1={y(t)} y2={y(t)} stroke={C.line} strokeWidth="1" />);
    grid.push(
      <text key={`v${t}`} x={ML - 10} y={y(t) + 4} fontSize="12" fill={C.text} textAnchor="end">
        {fmt(t, digits)}
      </text>,
    );
  }
  let d = '';
  let area = `M${xh(0, n)},${y(0)}`;
  prices.forEach((p, h) => {
    d += `${h === 0 ? 'M' : 'L'}${xh(h, n)},${y(p)} L${xh(h + 1, n)},${y(p)} `;
    area += ` L${xh(h, n)},${y(p)} L${xh(h + 1, n)},${y(p)}`;
  });
  area += ` L${xh(prices.length, n)},${y(0)} Z`;
  return (
    <ChartBox n={n} lines={lines}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title} xmlns="http://www.w3.org/2000/svg">
        {grid}
        <path d={area} fill={C.ice} fillOpacity="0.55" />
        <path d={d} fill="none" stroke={C.navy} strokeWidth="2" />
        {axis.now !== null ? (
          <NowLine n={n} at={axis.now} top={MT} bottom={MT + ph} label={axis.nowLabel} />
        ) : null}
        <HourAxis n={n} y={MT + ph} labels={axis.labels} />
      </svg>
    </ChartBox>
  );
}

export function BatteryChart({
  axis,
  plan,
  fact,
  soc,
  windows,
  fmt,
  title,
  lines,
}: {
  axis: DayAxis;
  /** kW per hour, + discharge / − charge; null without a plan. */
  plan: readonly number[] | null;
  fact: readonly (number | null)[];
  /** Last SOC of each hour, %. */
  soc: readonly (number | null)[];
  windows: readonly DeyeWindow[] | null;
  fmt: Fmt;
  title: string;
  lines: (h: number) => ReactNode[];
}) {
  const { n } = axis;
  const H = 300;
  const MT = 16;
  const ph = H - MT - 26;
  const mid = MT + ph / 2;
  const bands: ReactNode[] = [];
  windows?.forEach((w, i) => {
    const x0 = xh(w.start_hour, n);
    const x1 = xh(windows[i + 1]?.start_hour ?? n, n);
    if (i % 2 === 1)
      bands.push(<rect key={`b${w.start_hour}`} x={x0} y={MT} width={x1 - x0} height={ph} fill={C.band} />);
    bands.push(
      <line key={`bl${w.start_hour}`} x1={x0} x2={x0} y1={MT} y2={MT + ph} stroke={C.line} strokeWidth="1" />,
    );
    bands.push(
      <text
        key={`bt${w.start_hour}`}
        x={x0 + 5}
        y={MT + ph - 6}
        fontSize="11.5"
        fill={C.text}
        fontWeight="500"
      >
        {i + 1}
      </text>,
    );
  });
  const all = [...(plan ?? []), ...fact.filter((v): v is number => v !== null)].map(Math.abs);
  const max = niceMax(Math.max(...all, 1), 4);
  const yk = (v: number) => mid - (v / max) * (ph / 2);
  const ys = (p: number) => MT + ph - (p / 100) * ph;
  const grid: ReactNode[] = [];
  for (let k = -2; k <= 2; k += 1) {
    const t = (k * max) / 2;
    grid.push(
      <line
        key={`g${k}`}
        x1={ML}
        x2={W - MR}
        y1={yk(t)}
        y2={yk(t)}
        stroke={k === 0 ? C.slate : C.line}
        strokeWidth="1"
      />,
    );
    grid.push(
      <text key={`v${k}`} x={ML - 10} y={yk(t) + 4} fontSize="12" fill={C.text} textAnchor="end">
        {fmt(Math.abs(t), stepDigits(max / 2))}
      </text>,
    );
  }
  for (const p of [0, 50, 100]) {
    grid.push(
      <text key={`s${p}`} x={W - MR + 10} y={ys(p) + 4} fontSize="12" fill={C.text}>
        {p} %
      </text>,
    );
  }
  const slot = PW / n;
  const bw = slot * 0.36;
  const bars: ReactNode[] = [];
  for (let h = 0; h < n; h += 1) {
    const x0 = xh(h, n) + slot * 0.12;
    const v = plan?.[h];
    if (v !== undefined && Math.abs(v) > 0.05) {
      const color = v > 0 ? C.navy : C.sky;
      bars.push(
        <rect
          key={`p${h}`}
          x={x0}
          y={Math.min(yk(v), mid)}
          width={bw}
          height={Math.abs(yk(v) - mid)}
          rx="2"
          fill={color}
          fillOpacity="0.22"
          stroke={color}
          strokeWidth="1"
        />,
      );
    }
    const f = fact[h];
    if (f !== null && f !== undefined && Math.abs(f) > 0.05) {
      bars.push(
        <rect
          key={`f${h}`}
          x={x0 + (plan ? bw + 2 : bw / 2)}
          y={Math.min(yk(f), mid)}
          width={bw}
          height={Math.abs(yk(f) - mid)}
          rx="2"
          fill={f > 0 ? C.navy : C.sky}
        />,
      );
    }
  }
  // The last SOC of an hour sits at its end; of the hour in progress — at «now».
  let socPath = '';
  let pen = false;
  soc.forEach((p, h) => {
    if (p === null) {
      pen = false;
      return;
    }
    const at = axis.now !== null && h + 1 > axis.now ? Math.max(h, axis.now) : h + 1;
    socPath += `${pen ? 'L' : 'M'}${xh(at, n)},${ys(p)} `;
    pen = true;
  });
  return (
    <ChartBox n={n} lines={lines}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title} xmlns="http://www.w3.org/2000/svg">
        {bands}
        {grid}
        {bars}
        {socPath ? <path d={socPath} fill="none" stroke={C.navy} strokeWidth="2" /> : null}
        {axis.now !== null ? (
          <NowLine n={n} at={axis.now} top={MT} bottom={MT + ph} label={axis.nowLabel} />
        ) : null}
        <HourAxis n={n} y={MT + ph} labels={axis.labels} />
      </svg>
    </ChartBox>
  );
}

export function ProfileChart({
  values,
  spread,
  labels,
  fmt,
  title,
  lines,
}: {
  values: readonly number[];
  spread: readonly ([number, number] | null)[];
  labels: readonly string[];
  fmt: Fmt;
  title: string;
  lines: (h: number) => ReactNode[];
}) {
  const n = values.length;
  const H = 210;
  const MT = 14;
  const ph = H - MT - 26;
  // A small load (30 W) keeps its own scale instead of a floor of 0,5 kW.
  const top = Math.max(...values, ...spread.map((s) => s?.[1] ?? 0), 0.05) * 1.08;
  const { step, max, digits } = valueScale(top, 4);
  const y = (v: number) => MT + ph - (v / max) * ph;
  const g: ReactNode[] = [];
  for (const t of axisValues({ step, max })) {
    g.push(<line key={`g${t}`} x1={ML} x2={W - MR} y1={y(t)} y2={y(t)} stroke={C.line} strokeWidth="1" />);
    g.push(
      <text key={`v${t}`} x={ML - 10} y={y(t) + 4} fontSize="12" fill={C.text} textAnchor="end">
        {fmt(t, digits)}
      </text>,
    );
  }
  const slot = PW / n;
  const bw = slot * 0.56;
  for (let h = 0; h < n; h += 1) {
    const v = values[h] ?? 0;
    const x = xh(h, n) + (slot - bw) / 2;
    const cx = xh(h, n) + slot / 2;
    g.push(
      <rect
        key={`m${h}`}
        x={x}
        y={y(v)}
        width={bw}
        height={MT + ph - y(v)}
        rx="2"
        fill={C.navy}
        fillOpacity="0.85"
      />,
    );
    const s = spread[h];
    if (s) {
      g.push(
        <line key={`s${h}`} x1={cx} x2={cx} y1={y(s[1])} y2={y(s[0])} stroke={C.slate} strokeWidth="1.5" />,
      );
      g.push(
        <line
          key={`h${h}`}
          x1={cx - 4}
          x2={cx + 4}
          y1={y(s[1])}
          y2={y(s[1])}
          stroke={C.slate}
          strokeWidth="1.5"
        />,
      );
      g.push(
        <line
          key={`o${h}`}
          x1={cx - 4}
          x2={cx + 4}
          y1={y(s[0])}
          y2={y(s[0])}
          stroke={C.slate}
          strokeWidth="1.5"
        />,
      );
    }
  }
  return (
    <ChartBox n={n} lines={lines}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title} xmlns="http://www.w3.org/2000/svg">
        {g}
        <HourAxis n={n} y={MT + ph} labels={labels} />
      </svg>
    </ChartBox>
  );
}
